import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, Field } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import type { PersonOption } from './data';

/** Buscar a una persona por nombre o correo (con el correo exacto, de cualquier organización del tenant). */
export function PersonPicker({ id, label, value, onChange }: { id: string; label: string; value: { id: string; fullName: string } | null; onChange: (p: { id: string; fullName: string } | null) => void }) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const people = useQuery({
    queryKey: ['licencias', 'usuarios', debounced],
    queryFn: () => api<PersonOption[]>(`/api/licencias/usuarios?${new URLSearchParams({ buscar: debounced })}`),
    enabled: debounced.length >= 2 && !value,
  });

  if (value)
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="flex items-center gap-3 text-sm">
          <span className="text-slate-900">{value.fullName}</span>
          <Button variant="link" className="text-sm" onClick={() => onChange(null)}>
            {t('li.cambiar')}
          </Button>
        </p>
      </div>
    );
  return (
    <div className="space-y-1">
      <Field id={id} type="search" label={label} placeholder={t('li.buscar_persona')} value={search} onChange={(e) => setSearch(e.target.value)} />
      {debounced.length >= 2 && people.data && (
        <ul className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200" aria-label={t('li.personas')}>
          {people.data.length === 0 ? (
            <li className="px-3 py-2 text-sm text-slate-500">{t('li.sin_personas')}</li>
          ) : (
            people.data.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => {
                    onChange({ id: p.id, fullName: p.fullName });
                    setSearch('');
                  }}
                >
                  <span className="font-medium text-slate-900">{p.fullName}</span>
                  <span className="block text-xs text-slate-500">
                    {p.email}
                    {p.organization && ` · ${p.organization}`}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
